import { useState, useRef, useEffect } from 'react'
import { Reply, Forward, Star, Trash2, Archive, Code, FileText, Maximize2, X, User, Building2, Loader2 } from 'lucide-react'
import { Email } from '../../types/email'
import { getAccountById } from '../../data/mockEmails'
import { setSenderHumanStatus } from '../../services/vendorService'

interface EmailViewProps {
  email: Email | null
  userId?: string
  senderIsHuman?: boolean  // Current classification: true = person, false = vendor, undefined = unknown
  onSenderStatusChange?: () => void
}

function formatFullDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

function formatShortDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

export default function EmailView({ email, userId, senderIsHuman, onSenderStatusChange }: EmailViewProps) {
  const [viewMode, setViewMode] = useState<'text' | 'html'>('html')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isSettingStatus, setIsSettingStatus] = useState(false)
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const handleSetSenderStatus = async (isHuman: boolean) => {
    if (!email || !userId) return

    setIsSettingStatus(true)
    setStatusMessage(null)
    try {
      await setSenderHumanStatus(userId, email.from.email, email.from.name, isHuman)
      setStatusMessage({
        type: 'success',
        text: isHuman
          ? `Marked "${email.from.name}" as human`
          : `Marked "${email.from.name}" as vendor`
      })
      onSenderStatusChange?.()
      setTimeout(() => setStatusMessage(null), 3000)
    } catch (error) {
      console.error('Failed to set sender status:', error)
      setStatusMessage({ type: 'error', text: 'Failed to update sender' })
      setTimeout(() => setStatusMessage(null), 3000)
    } finally {
      setIsSettingStatus(false)
    }
  }

  // Update iframe content when email or view mode changes
  useEffect(() => {
    if (iframeRef.current && email?.htmlBody && viewMode === 'html') {
      const doc = iframeRef.current.contentDocument
      if (doc) {
        doc.open()
        doc.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <base target="_blank">
              <meta name="viewport" content="width=device-width, initial-scale=1">
              <style>
                body {
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                  font-size: 14px;
                  line-height: 1.5;
                  color: #374151;
                  padding: 12px;
                  margin: 0;
                  background: white;
                  -webkit-overflow-scrolling: touch;
                }
                img {
                  max-width: 100%;
                  height: auto;
                }
                a {
                  color: #4f46e5;
                }
                table {
                  max-width: 100%;
                }
                @media (min-width: 768px) {
                  body {
                    padding: 16px;
                  }
                }
              </style>
            </head>
            <body>${email.htmlBody}</body>
          </html>
        `)
        doc.close()
      }
    }
  }, [email?.htmlBody, viewMode])

  if (!email) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 text-gray-500 p-4">
        <div className="text-center">
          <p className="text-base md:text-lg">Select an email to view</p>
          <p className="text-xs md:text-sm mt-1">Choose from your inbox on the left</p>
        </div>
      </div>
    )
  }

  const account = getAccountById(email.accountId)
  const hasHtml = !!email.htmlBody

  // Fullscreen modal for HTML view
  if (isFullscreen && hasHtml) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 md:p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[95vh] md:h-[90vh] flex flex-col">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-3 md:p-4 border-b border-gray-200">
            <div className="min-w-0 flex-1 pr-2">
              <h3 className="font-semibold text-gray-900 text-sm md:text-base truncate">{email.subject}</h3>
              <p className="text-xs md:text-sm text-gray-500 truncate">From: {email.from.name} &lt;{email.from.email}&gt;</p>
            </div>
            <button
              onClick={() => setIsFullscreen(false)}
              className="p-2.5 md:p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <X size={20} />
            </button>
          </div>

          {/* Modal Content */}
          <div className="flex-1 overflow-hidden">
            <iframe
              ref={iframeRef}
              title="Email content"
              className="w-full h-full border-0"
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col bg-white overflow-hidden">
      {/* Header */}
      <div className="p-3 sm:p-4 md:p-6 border-b border-gray-200">
        {/* Subject and action buttons */}
        <div className="flex items-start justify-between gap-2 mb-3 md:mb-4">
          <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 line-clamp-2 md:line-clamp-none">{email.subject}</h2>
          <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
            <button
              className="p-2.5 md:p-2 text-gray-400 hover:text-yellow-500 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 flex items-center justify-center"
              title="Star"
            >
              <Star size={18} className={email.isStarred ? 'fill-yellow-400 text-yellow-400' : ''} />
            </button>
            <button
              className="p-2.5 md:p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 flex items-center justify-center"
              title="Archive"
            >
              <Archive size={18} />
            </button>
            <button
              className="p-2.5 md:p-2 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 flex items-center justify-center"
              title="Delete"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>

        {/* Sender info */}
        <div className="flex items-start sm:items-center gap-2 sm:gap-3">
          {/* Avatar */}
          <div
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-medium text-sm sm:text-base flex-shrink-0"
            style={{ backgroundColor: account?.color || '#6366f1' }}
          >
            {email.from.name.charAt(0).toUpperCase()}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
              <span className="font-medium text-gray-900 text-sm sm:text-base truncate">{email.from.name}</span>
              <span className="text-xs sm:text-sm text-gray-500 truncate">&lt;{email.from.email}&gt;</span>
            </div>
            <div className="text-xs sm:text-sm text-gray-500 truncate">
              to {email.to.map((t) => t.name || t.email).join(', ')}
            </div>
          </div>

          {/* Date - hidden on mobile, shown on larger screens */}
          <div className="hidden md:block text-sm text-gray-500 flex-shrink-0">{formatFullDate(email.date)}</div>
        </div>

        {/* Mobile date display */}
        <div className="md:hidden mt-2 text-xs text-gray-500">{formatShortDate(email.date)}</div>

        {/* Account indicator and sender classification */}
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: account?.color || '#6366f1' }}
            />
            <span className="truncate">Received in {account?.email}</span>
          </div>

          {/* Mark as Human/Vendor toggle */}
          {userId && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              {statusMessage && (
                <span className={`text-xs ${statusMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {statusMessage.text}
                </span>
              )}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => handleSetSenderStatus(true)}
                  disabled={isSettingStatus || senderIsHuman === true}
                  className={`flex items-center gap-1.5 px-3 py-2 sm:py-1.5 text-sm rounded-md transition-colors min-h-[44px] sm:min-h-0 ${
                    senderIsHuman === true
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                  } disabled:opacity-70`}
                  title="Mark sender as human"
                >
                  {isSettingStatus ? <Loader2 size={14} className="animate-spin" /> : <User size={14} />}
                  Human
                </button>
                <button
                  onClick={() => handleSetSenderStatus(false)}
                  disabled={isSettingStatus || senderIsHuman === false}
                  className={`flex items-center gap-1.5 px-3 py-2 sm:py-1.5 text-sm rounded-md transition-colors min-h-[44px] sm:min-h-0 ${
                    senderIsHuman === false
                      ? 'bg-indigo-500 text-white shadow-sm'
                      : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                  } disabled:opacity-70`}
                  title="Mark sender as a vendor (company)"
                >
                  {isSettingStatus ? <Loader2 size={14} className="animate-spin" /> : <Building2 size={14} />}
                  Vendor
                </button>
              </div>
            </div>
          )}
        </div>

        {/* View mode toggle */}
        {hasHtml && (
          <div className="mt-3 md:mt-4 flex flex-wrap items-center gap-2">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('html')}
                className={`flex items-center gap-1.5 px-3 py-2 sm:py-1.5 text-sm rounded-md transition-colors min-h-[44px] sm:min-h-0 ${
                  viewMode === 'html'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Code size={14} />
                HTML
              </button>
              <button
                onClick={() => setViewMode('text')}
                className={`flex items-center gap-1.5 px-3 py-2 sm:py-1.5 text-sm rounded-md transition-colors min-h-[44px] sm:min-h-0 ${
                  viewMode === 'text'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FileText size={14} />
                Text
              </button>
            </div>
            {viewMode === 'html' && (
              <button
                onClick={() => setIsFullscreen(true)}
                className="flex items-center gap-1.5 px-3 py-2 sm:py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
              >
                <Maximize2 size={14} />
                <span className="hidden sm:inline">Fullscreen</span>
                <span className="sm:hidden">Full</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Body - Scrollable content area */}
      <div className="flex-1 overflow-hidden min-h-0">
        {viewMode === 'html' && hasHtml ? (
          <iframe
            ref={iframeRef}
            title="Email content"
            className="w-full h-full border-0"
            sandbox="allow-same-origin"
          />
        ) : (
          <div className="h-full overflow-y-auto overscroll-contain p-3 sm:p-4 md:p-6 -webkit-overflow-scrolling-touch">
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-700 text-sm md:text-base">
              {email.body}
            </div>
          </div>
        )}
      </div>

      {/* Actions - Touch-friendly buttons */}
      <div className="p-3 sm:p-4 border-t border-gray-200 flex gap-2 sm:gap-3">
        <button className="flex-1 sm:flex-none flex items-center justify-center sm:justify-start gap-2 px-4 py-3 sm:py-2 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white rounded-lg font-medium transition-colors min-h-[44px]">
          <Reply size={18} />
          Reply
        </button>
        <button className="flex-1 sm:flex-none flex items-center justify-center sm:justify-start gap-2 px-4 py-3 sm:py-2 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors min-h-[44px]">
          <Forward size={18} />
          Forward
        </button>
      </div>
    </div>
  )
}
